import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, catchError, combineLatest, finalize, forkJoin, map, of, startWith, Subscription, switchMap, tap, timeout } from 'rxjs';
import { Helper } from 'src/app/accountancy/utilities/classes/helper.class';
import { Routes } from 'src/app/accountancy/utilities/classes/routes.class';
import { WaitHandler } from 'src/app/accountancy/utilities/classes/wait-handler.class';
import { ReimbursementInterface, ReimbursementItemHistory, ReimbursementItemInterface } from 'src/app/accountancy/utilities/interfaces/reimbursement.interface';
import { ReimbursementService } from '../reimbursement-service/reimbursement.service';
import { ManageApproversService } from '../../../account-management/manage-approvers/manage-approvers-service/manage-approvers.service';
import { AccSimpleInputTextFormService } from 'src/app/accountancy/custom-components/acc-simple-input-text-form/acc-simple-input-text-form-service/acc-simple-input-text-form.service';
import { AccMessageDialogService } from 'src/app/accountancy/custom-components/acc-message-dialog/acc-message-dialog-service/acc-message-dialog.service';
import { LoginService } from 'src/app/accountancy/authentication/login/login-service/login.service';
import { GlobalApiService } from 'src/app/accountancy/utilities/services/global-api/global-api.service';
import { DisbursementVoucherComponent, DisbursementVoucherPassedData } from '../../disbursement-voucher/disbursement-voucher.component';
import { MatDialog } from '@angular/material/dialog';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { AccConfirmationService } from 'src/app/accountancy/custom-components/acc-confirmation/acc-confirmation-service/acc-confirmation.service';
import { DisbursedService } from '../../disbursed-service/disbursed.service';
import { AccountingApiService } from 'src/app/accountancy/utilities/services/accounting-api/accounting-api.service';
import { ReimbursementFormComponent } from '../reimbursement-form/reimbursement-form.component';
import { ViewReimbursementItemHistoryComponent } from '../view-reimbursement-item-history/view-reimbursement-item-history.component';
import { LocalStorageApiService } from 'src/app/accountancy/utilities/services/local-storage-api/local-storage-api.service';

@Component({
  selector: 'app-view-reimbursement',
  templateUrl: './view-reimbursement.component.html',
  styleUrls: ['./view-reimbursement.component.scss']
})
export class ViewReimbursementComponent {

  public readonly Helper = Helper;
  public readonly routes = Routes;
  public readonly reimId = Helper.getIdOnSearchParam(this._aRoute, 'id');

  public readonly faReimItems = new FormArray<FormGroup<{
    ItemId: FormControl<number | undefined | null>;
    ApprovedAmount: FormControl<number | undefined | null>;
    Remarks: FormControl<string | undefined | null>;
    Account: FormControl<string | undefined | null>;
  }>>([]);

  public readonly waitHandler = new WaitHandler();

  public readonly currentReim$ = new BehaviorSubject<ReimbursementInterface | undefined>(undefined);
  private readonly reloadCurrentReim$ = new BehaviorSubject<undefined | void>(undefined);
  private readonly _subsGetCurrentReim: Subscription = this._getSubsGetCurrentReim();

  public readonly chartAccList$ = new BehaviorSubject<string[]>([]);
  private readonly _getChartAccListSubs: Subscription = this._getChartAccList();

  public readonly readInfo01$ = new BehaviorSubject<boolean>(false);

  constructor(
    private _aRoute: ActivatedRoute,
    private _router: Router,
    private _reimS: ReimbursementService,
    private _mApprS: ManageApproversService,
    private _simpleFormS: AccSimpleInputTextFormService,
    private _msg: AccMessageDialogService,
    private _confirm: AccConfirmationService,
    private _matDialog: MatDialog,
    private _globalApiS: GlobalApiService,
    private _disburseS: DisbursedService,
    private _accApi: AccountingApiService,
    private _localStorageS: LocalStorageApiService,

    public loginS: LoginService,
  ) { }

  public ngOnInit(): void {
    if (!this.reimId) this._router.navigate([this.routes.reimbursement]);
  }

  public ngOnDestroy(): void {
    this._subsGetCurrentReim.unsubscribe();
    this._getChartAccListSubs.unsubscribe();
  }

  public setAccountItem(index: number) {
    const fcAccount = this.getFormGroup(index).controls.Account;
    const isRequesting$ = new BehaviorSubject<boolean>(false);

    const categoryList$ = new BehaviorSubject<{ value: any, key: string }[]>([]);
    const accountList$ = new BehaviorSubject<{ value: any, key: string }[]>([]);
    const filteredAccountList$ = new BehaviorSubject<{ value: any, key: string }[]>([]);

    const subsCateg = of(null).pipe(
      tap(() => isRequesting$.next(true)),
      switchMap(() => this._accApi.chartOfAccounts.category.getCategoryList(0, 99999).pipe(
        finalize(() => {
          isRequesting$.next(false);
          subsCateg.unsubscribe();
        }),
        map(data => data.body?.data ?? []),
        map(data => data.map(item => ({ value: item.Name, key: item.Name }))),
        tap(data => categoryList$.next(data))
      ))
    ).subscribe();

    const subsAcc = of(null).pipe(
      tap(() => isRequesting$.next(true)),
      switchMap(() => this._accApi.chartOfAccounts.getChartOfAccountList(0, 99999).pipe(
        finalize(() => {
          isRequesting$.next(false);
          subsAcc.unsubscribe();
        }),
        map(data => data.body?.data ?? []),
        map(data => data.map(item => ({ value: { Account: `(${item.Code}) ${item.Account}`, Category: item.Category.Name }, key: `(${item.Code}) ${item.Account}` }))),
        tap(data => accountList$.next(data))
      ))
    ).subscribe();

    const onCategoryChange = (categ: string) => {
      filteredAccountList$.next(
        accountList$.getValue().filter(acc => acc.value.Category === categ).map(acc => ({ key: acc.key, value: acc.value.Account }))
      );
    };

    this._simpleFormS.open({
      isRequesting$,
      title: "Set Account",
      controls: [
        { name: "Category", inputType: "select", colClassName: "col-span-12", selectOptions$: categoryList$, onValueChanges: onCategoryChange },
        { name: "Account", inputType: "search", colClassName: "col-span-12", selectOptions$: filteredAccountList$ },
      ],
      onSubmit: (formData, ref) => {
        fcAccount.patchValue(formData["Account"]);
        ref?.close();
      }
    });
  }

  public approveRequest() {
    const items = this.currentReim$.getValue()?.Items ?? []
    const itemWithStatus = items.filter(item => item.Status);
    if (items.length != itemWithStatus.length) {
      this._msg.open({
        title: "Error occurred.",
        message: "All items must have a status.",
        messageType: "text-red-400"
      });
      return;
    }

    const isRequesting$ = new BehaviorSubject<boolean>(false);
    const ref = this._simpleFormS.open({
      isRequesting$,
      title: "Approve Request",
      formClass: "min-w-[400px]",
      controls: [
        { name: "Remarks", inputType: "text", colClassName: "col-span-12" }
      ],
      onSubmit: formData => {
        const subs: Subscription = of(null).pipe(
          tap(() => {
            this.waitHandler.newRequest();
            isRequesting$.next(true);
          }),
          switchMap(() => this._reimS.approveRequest(this.reimId, formData["Remarks"]).pipe(
            finalize(() => {
              this.waitHandler.finishRequest();
              isRequesting$.next(false);
              ref.close();
              subs.unsubscribe();
            }),
          ))
        ).subscribe();
      },
    });

    const subsRef: Subscription = ref.afterClosed().pipe(
      finalize(() => {
        this.reloadCurrentReim$.next();
        subsRef.unsubscribe();
      }),
    ).subscribe();
  }

  public disapproveRequest() {
    const isRequesting$ = new BehaviorSubject<boolean>(false);
    const ref = this._simpleFormS.open({
      isRequesting$,
      title: "Disapprove Request",
      formClass: "min-w-[400px]",
      controls: [
        { name: "Remarks", inputType: "text", colClassName: "col-span-12" }
      ],
      onSubmit: formData => {
        const subs: Subscription = of(null).pipe(
          tap(() => {
            this.waitHandler.newRequest();
            isRequesting$.next(true);
          }),
          switchMap(() => this._reimS.disapproveRequest(this.reimId, formData["Remarks"]).pipe(
            finalize(() => {
              this.waitHandler.finishRequest();
              isRequesting$.next(false);
              ref.close();
              subs.unsubscribe();
            }),
          ))
        ).subscribe();
      },
    });

    const subsRef: Subscription = ref.afterClosed().pipe(
      finalize(() => {
        this.reloadCurrentReim$.next();
        subsRef.unsubscribe();
      }),
    ).subscribe();
  }

  public approveItem(index: number) {
    const formGroup = this.getFormGroup(index);
    if (!formGroup) return;

    const formData = formGroup.value;
    const errorControls = Helper.getInvalidControls(formGroup);
    if (errorControls.length) {
      this._msg.open({
        title: "Error occurred.",
        messageType: "text-red-400",
        message: [
          "Invalid values found in the fields below: ",
          ...errorControls.map(c => `- ${c.key}`),
        ]
      })
      return;
    }

    this._confirm.open({
      title: "Confirm Approval (Approved)",
      message: [
        `Are you sure you want to Approved this item with ₱ ${Number(formData["ApprovedAmount"])?.toFixed(2)} amount?`,
        "You cannot undo this action."
      ],
      onAnswer: (yes, ref) => {
        if (yes) {
          const subs: Subscription = of(null).pipe(
            tap(() => this.waitHandler.newRequest()),
            switchMap(() => this._reimS.updateItemStatus(
              this.reimId,
              formData["ItemId"] ?? -1,
              {
                Remarks: formData["Remarks"] ?? "",
                ApprovedAmount: formData["ApprovedAmount"] ?? -1,
                Status: "Approved",
                Account: formData["Account"],
              }
            ).pipe(
              finalize(() => {
                this.waitHandler.finishRequest();
                this.reloadCurrentReim$.next();
                subs.unsubscribe();
              })
            ))
          ).subscribe();
        }

        ref.close();
      }
    });
  }

  public disapproveItem(index: number) {
    const formGroup = this.getFormGroup(index);
    if (!formGroup) return;

    const formData = formGroup.value;

    this._confirm.open({
      title: "Confirm Approval (Disapproved)",
      message: [
        "Are you sure you want to Disapproved this item?",
        "You cannot undo this action."
      ],
      onAnswer: (yes, ref) => {
        if (yes) {
          const subs: Subscription = of(null).pipe(
            tap(() => this.waitHandler.newRequest()),
            switchMap(() => this._reimS.updateItemStatus(
              this.reimId,
              formData["ItemId"] ?? -1,
              {
                Remarks: formData["Remarks"] ?? "",
                Status: "Disapproved",
                ApprovedAmount: undefined,
                Account: undefined,
              }
            ).pipe(
              finalize(() => {
                this.waitHandler.finishRequest();
                this.reloadCurrentReim$.next();
                subs.unsubscribe();
              })
            ))
          ).subscribe();
        }

        ref.close();
      }
    });
  }

  public viewItemHistory(index: number, data: ReimbursementItemHistory[]) {
    const ref = this._matDialog.open(ViewReimbursementItemHistoryComponent, { data });
  }

  public updateReim() {
    const ref = this._matDialog.open(ReimbursementFormComponent, { data: this.currentReim$.getValue() });
    const subsRefOnClose = ref.afterClosed().pipe(
      finalize(() => {
        this.reloadCurrentReim$.next();
        subsRefOnClose.unsubscribe();
      })
    ).subscribe();
  }

  public getFormGroup(index: number) {
    return this.faReimItems.controls[index];
  }

  public viewVoucher() {
    this._localStorageS.object.set("currentVoucher", {
      type: "Reimbursement",
      disbursementVoucher: this.currentReim$.getValue()?.Voucher,
      controlNumber: this.reimId,
    });

    const url = this._router.serializeUrl(this._router.createUrlTree(['home', 'disbursement', 'disbursement-voucher']));
    const windowFeatures = 'width=800,height=600,resizable,scrollbars';
    window.open(url, '_blank', windowFeatures);
  }

  public generateVoucher() {
    const currReim = this.currentReim$.getValue();
    const form = this._matDialog.open(DisbursementVoucherComponent, {
      data: {
        Type: "Reimbursement",
        CntrlNumber: this.reimId,
        ApprovedAmount: currReim?.OverallApprovedAmount,
        ReimItems: currReim?.Items,
      } as DisbursementVoucherPassedData,
      disableClose: true
    });

    const onClosedSubs: Subscription = form.afterClosed().pipe(
      finalize(() => {
        this.reloadCurrentReim$.next();
        onClosedSubs.unsubscribe();
      }),
    ).subscribe();
  }

  private _newItemFg(currItem: ReimbursementItemInterface) {
    const isForFinancerApproval = this.currentReim$.getValue()?.AddChartOfAccountItem;
    const approvedAmount = currItem.ApprovedAmount || currItem.VatableSales + currItem.VAT;

    const fcItemId = Helper.reactiveForms.control<number | undefined | null>(currItem.Id, [Validators.required]);
    const fcApprovedAmount = Helper.reactiveForms.control<number | undefined | null>(approvedAmount, [Validators.required, Validators.min(1), Validators.max(currItem.VatableSales + currItem.VAT)]);
    const fcRemarks = Helper.reactiveForms.control<string | undefined | null>(undefined);
    const fcAccount = Helper.reactiveForms.control<string | undefined | null>(currItem.Account, isForFinancerApproval ? [Validators.required] : []);

    const reim = this.currentReim$.getValue();
    const currStatus = currItem.Status;
    const itemHaveStatus = (currStatus && (currStatus == "Approved" || currStatus == "Disapproved")) ?? false;
    const userCanUpdateStatus = reim && (!currStatus && !reim.GenerateVoucher && !reim.Voucher && reim.update_status);
    if (itemHaveStatus || !userCanUpdateStatus) {
      if (currStatus) fcRemarks.patchValue(currItem.History[0]?.Remarks, { emitEvent: false });
      if (!currItem.ApprovedAmount && !currStatus) fcApprovedAmount.patchValue(0, { emitEvent: false });

      fcApprovedAmount.disable();
      fcRemarks.disable();
      fcAccount.disable();
    }

    return new FormGroup({
      ItemId: fcItemId,
      ApprovedAmount: fcApprovedAmount,
      Remarks: fcRemarks,
      Account: fcAccount,
    });
  }

  private _getChartAccList() {
    return of(null).pipe(
      tap(() => this.waitHandler.newRequest()),
      switchMap(() => this._accApi.chartOfAccounts.getChartOfAccountList(0, 9999).pipe(
        finalize(() => this.waitHandler.finishRequest()),
        map(values => values.body?.data),
        tap(values => {
          const list = values?.map(v => `(${v.Code}) ${v.Category.Name} - ${v.Account}`) ?? [];

          this.chartAccList$.next(list);
        })
      ))
    ).subscribe();
  }

  private _getSubsGetCurrentReim() {
    return combineLatest([
      this.reloadCurrentReim$.pipe(startWith()),
    ]).pipe(
      tap(() => {
        this.waitHandler.newRequest();
        this.faReimItems.clear();
      }),
      switchMap(() => this._reimS.getSingleReimbursement(this.reimId).pipe(
        finalize(() => this.waitHandler.finishRequest()),
      )),
      switchMap(rawData => {
        const rawItems = rawData?.Items ?? [];
        const getItemsProofURL = rawItems.map(item => of(null).pipe(
          tap(() => this.waitHandler.newRequest()),
          switchMap(() => this._globalApiS.getFileUrl(item.Proof, "Receipts").pipe(
            finalize(() => this.waitHandler.finishRequest()),
            map(proof => ({
              itemId: item.Id,
              proof: proof.body?.url ?? ''
            }))
          ))
        ))
        return forkJoin(getItemsProofURL).pipe(
          map(proof => rawItems.map(item => {
            const newProof = proof.find(p => p.itemId === item.Id);
            if (newProof) return ({ ...item, ProofURL: newProof.proof })
            else return item;
          })),
          map(newItems => newItems.map(item => ({
            ...item,
            History: Helper.array.sortByPropertyName(item.History, "created_at", false)
          } as ReimbursementItemInterface))),
          map(orderedItems => ({
            ...rawData,
            Items: orderedItems,
            history: Helper.array.sortByPropertyName(rawData?.history, "created_at", false),
          } as ReimbursementInterface)),
        );
      }),
      tap(values => this.currentReim$.next(values)),
      tap(values => values.Items.forEach(item => {
        const fgItem = this._newItemFg(item);
        this.faReimItems.push(fgItem);
      })),
      catchError(e => this._globalApiS.handleSingleGetError(() => this._router.navigate([this.routes.reimbursement]))),
    ).subscribe();
  }

}
